"use client";

import { useMemo } from "react";
import { HiMiniChevronDown, HiMiniChevronLeft, HiMiniChevronRight } from "react-icons/hi2";

export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];
export const PAGINATION_MIN_TOTAL = 21;

export function buildPaginationItems(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
    if (totalPages <= 6) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
        return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage >= totalPages - 2) {
        return [1, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

type TablePaginationProps = {
    totalCount: number;
    totalCountLabel: string;
    currentPage: number;
    totalPages: number;
    pageSize: PageSizeOption;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: PageSizeOption) => void;
};

export default function TablePagination({
    totalCount,
    totalCountLabel,
    currentPage,
    totalPages,
    pageSize,
    onPageChange,
    onPageSizeChange,
}: TablePaginationProps) {
    const paginationItems = useMemo(
        () => buildPaginationItems(currentPage, totalPages),
        [currentPage, totalPages]
    );

    if (totalCount < PAGINATION_MIN_TOTAL) {
        return null;
    }

    return (
        <div className="mt-6 flex flex-col items-center gap-4 pb-2">
            <p className="text-sm font-medium text-[#4E4E4E]">{totalCountLabel}</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
                <label className="flex items-center gap-2 text-sm text-[#4E4E4E]">
                    <div className="relative">
                        <select
                            value={pageSize}
                            onChange={(event) => onPageSizeChange(Number(event.target.value) as PageSizeOption)}
                            className="h-8 min-w-[64px] cursor-pointer appearance-none rounded-md border border-[#d6c844] bg-white pl-2.5 pr-8 text-xs text-[#2B2B2B] outline-none"
                        >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <HiMiniChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-600" />
                    </div>
                </label>
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-[#d6c844] bg-white text-[#2B2B2B] transition hover:bg-[#fefce8] disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        <HiMiniChevronLeft className="text-base" />
                    </button>

                    {paginationItems.map((item, index) => {
                        if (item === "ellipsis") {
                            return (
                                <span
                                    key={`ellipsis_${index}`}
                                    className="flex h-8 w-8 items-center justify-center rounded-md border border-[#d6c844] bg-white text-sm font-semibold text-[#2B2B2B]"
                                >
                                    ...
                                </span>
                            );
                        }

                        const isActive = item === currentPage;
                        return (
                            <button
                                key={item}
                                type="button"
                                onClick={() => onPageChange(item)}
                                className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border text-sm font-medium transition ${isActive
                                    ? "border-[#d6c844] bg-[#FDEF5C] text-[#2B2B2B] shadow-sm"
                                    : "border-[#d6c844] bg-white text-[#2B2B2B] hover:bg-[#fefce8]"
                                    }`}
                            >
                                {item}
                            </button>
                        );
                    })}

                    <button
                        type="button"
                        onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-[#d6c844] bg-white text-[#2B2B2B] transition hover:bg-[#fefce8] disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        <HiMiniChevronRight className="text-base" />
                    </button>
                </div>
            </div>
        </div>
    );
}
